import React, { FC, HTMLProps, ReactNode, useMemo } from "react";
import cn from "classnames";
import {
  Button,
  Tag,
  Divider,
  Tooltip,
  DefaultImage,
  Skeleton,
  Money,
} from "../../components";
import stl from "./index.module.scss";
import { Package } from "../../store/models";
import { isPresent } from "../../utils";
import { SelectedPackage } from "../../store/selected/selectedPackages";
import { useTranslation } from "react-i18next";
import { BsPencil, BsTrashFill } from "react-icons/bs";
import { AppConfig } from "../../config";
import { Dinero } from "dinero.js";
import CheckIco from "../../assets/iconsComponents/Check";
import { usePageSize } from "../../hooks";

const tKeyBase = AppConfig.translationFileNames.base;
const tKeyStepMain = AppConfig.translationFileNames.step_Main;

interface Props extends Omit<HTMLProps<HTMLDivElement>, "data" | "onSelect"> {
  data: Package;
  onEdit?: (data: Package) => void;
  onDelete?: (selectedData: SelectedPackage) => void;
  price?: {
    min: Dinero;
    max: Dinero;
  };
  pricePending?: boolean;
  disabled?: boolean;

  selectedData?: SelectedPackage;
}
const PackageCard: FC<Props> = ({
  data,
  onEdit,
  onDelete,
  price,
  pricePending,
  disabled,
  selectedData,
  ...props
}) => {
  const pageSize = usePageSize();
  const { t } = useTranslation([tKeyBase, tKeyStepMain]);
  const { image, description, name, supplements } = data;
  const selected = isPresent(selectedData);

  const remove = () => {
    if (!disabled && selectedData) onDelete?.(selectedData);
  };

  const edit = () => {
    if (!disabled) onEdit?.(data);
  };

  const activitiesOrderStr = JSON.stringify(selectedData?.activitiesOrder);
  const activities = useMemo<Package["activities"]>(() => {
    if (!selectedData?.activitiesOrder) return data.activities;

    const activities = [...data.activities];
    const activitiesOrder = [...selectedData.activitiesOrder];

    return activities.sort((a, b) => {
      const aIdx = activitiesOrder.findIndex(({ id }) => id === a.id);
      const bIdx = activitiesOrder.findIndex(({ id }) => id === b.id);
      if (aIdx > bIdx) {
        return 1;
      } else {
        return -1;
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activitiesOrderStr, data]);

  const renderPrice = (
    min?: Dinero,
    max?: Dinero,
    selectedPrice?: Dinero,
    pending?: boolean
  ) => {
    if (pending) {
      return <Skeleton width={50} className={stl.price} />;
    }
    if (isPresent(selectedPrice)) {
      return (
        <h4 className={stl.price}>
          <Money value={selectedPrice} />
        </h4>
      );
    }

    let priceNumbers: ReactNode = "";
    if (isPresent(min) && isPresent(max)) {
      if (min.hasSameAmount(max)) {
        priceNumbers = <Money value={min} />;
      } else {
        priceNumbers = (
          <>
            <Money value={min} />
            {" - "}
            <Money value={max} />
          </>
        );
      }
    }

    return (
      <>
        <h4 className={stl.price}>{priceNumbers}</h4>
        <span style={{ marginLeft: 5 }} onClick={(e) => e.stopPropagation()}>
          <Tooltip.Info>{t(`${tKeyStepMain}:varyPrices`)}</Tooltip.Info>
        </span>
      </>
    );
  };

  return (
    <div
      {...props}
      className={cn(stl.block, {
        [stl.selected]: selected,
        [stl.disabled]: disabled,
      })}
      tabIndex={0}
      onClick={edit}
      onKeyPress={({ key }) => {
        if (key === " " || key === "Enter") {
          edit();
        }
      }}
    >
      <CheckIco className={stl.check} />
      {image ? (
        <div className={stl.imgWrap}>
          <img src={image} alt="" />
        </div>
      ) : (
        <DefaultImage square />
      )}

      {activities.length > 0 && (
        <>
          <h5 className={stl.activitiesHeader}>Included activities</h5>

          {activities.map(({ name }, idx) => (
            <Tag key={name} icon={selectedData && idx + 1} variant={"primary"}>
              {name}
            </Tag>
          ))}

          {supplements.filter(({ visible }) => visible).length > 0 && (
            <Divider style={{ marginTop: 5 }} />
          )}
        </>
      )}

      {supplements.map(({ name, visible }) => {
        if (!visible) return null;
        return (
          <Tag key={name} variant={"primary"}>
            {name}
          </Tag>
        );
      })}

      <h3 className={stl.name}>{name}</h3>

      <div className={stl.priceWrap}>
        {renderPrice(
          price?.min,
          price?.max,
          selectedData?.availabilitySlot.totalPrice,
          pricePending
        )}
      </div>

      {description && <p>{description}</p>}

      {selected && (
        <>
          <Divider />

          {selectedData?.personTypes.map(({ name, quantity }) => (
            <Tag key={name} variant={"secondary"}>
              {name} x {quantity}
            </Tag>
          ))}
        </>
      )}

      {selected && (
        <div className={stl.actions}>
          <div>
            <Button
              icon={<BsPencil />}
              variant={"action"}
              hideContent={!pageSize.md}
              color={"editTransparent"}
              size={"small"}
              onClick={(e) => {
                e.stopPropagation();
                edit();
              }}
            >
              {t(`${tKeyBase}:actions.edit`)}
            </Button>
          </div>
          <div>
            <Button
              icon={<BsTrashFill />}
              variant={"action"}
              hideContent={!pageSize.md}
              color={"deleteTransparent"}
              size={"small"}
              onClick={(e) => {
                e.stopPropagation();
                remove();
              }}
            >
              {t(`${tKeyBase}:actions.remove`)}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PackageCard;
